import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
	createRdoActivity,
	deleteRdoActivity,
	type RdoActivityInput,
	reorderRdoActivities,
	updateRdoActivity,
} from "@/adapters/api";
import type { Rdo } from "../types.ts";
import { toRdoActivity } from "../utils/map.ts";
import { rdoQueryKey } from "./use-rdo.ts";

export function useRdoActivities(
	constructionId: string | undefined,
	rdoId: string | undefined,
) {
	const queryClient = useQueryClient();
	const key = rdoQueryKey(constructionId, rdoId);

	function requireIds() {
		if (!constructionId || !rdoId) throw new Error("missing rdo");
		return { constructionId, rdoId };
	}

	function invalidate() {
		queryClient.invalidateQueries({ queryKey: key });
	}

	const create = useMutation({
		mutationFn: async (input: RdoActivityInput) => {
			const ids = requireIds();
			return toRdoActivity(
				await createRdoActivity(ids.constructionId, ids.rdoId, input),
			);
		},
		onSuccess: invalidate,
	});

	const update = useMutation({
		mutationFn: async (variables: {
			activityId: string;
			input: RdoActivityInput;
		}) => {
			const ids = requireIds();
			return toRdoActivity(
				await updateRdoActivity(
					ids.constructionId,
					ids.rdoId,
					variables.activityId,
					variables.input,
				),
			);
		},
		onSuccess: (activity) => {
			queryClient.setQueryData<Rdo>(key, (current) =>
				current
					? {
							...current,
							activities: current.activities.map((item) =>
								item.id === activity.id ? activity : item,
							),
						}
					: current,
			);
		},
		onError: invalidate,
	});

	const remove = useMutation({
		mutationFn: async (activityId: string) => {
			const ids = requireIds();
			await deleteRdoActivity(ids.constructionId, ids.rdoId, activityId);
		},
		onSuccess: invalidate,
	});

	const reorder = useMutation({
		mutationFn: async (orderedIds: string[]) => {
			const ids = requireIds();
			return (
				await reorderRdoActivities(ids.constructionId, ids.rdoId, orderedIds)
			).map(toRdoActivity);
		},
		onMutate: async (orderedIds) => {
			await queryClient.cancelQueries({ queryKey: key });
			const previous = queryClient.getQueryData<Rdo>(key);
			if (previous) {
				const byId = new Map(previous.activities.map((a) => [a.id, a]));
				queryClient.setQueryData<Rdo>(key, {
					...previous,
					activities: orderedIds.flatMap((id) => {
						const activity = byId.get(id);
						return activity ? [activity] : [];
					}),
				});
			}
			return { previous };
		},
		onError: (_error, _ids, context) => {
			if (context?.previous) queryClient.setQueryData(key, context.previous);
		},
		onSettled: invalidate,
	});

	return { create, update, remove, reorder };
}
